"use client";

import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { useEffect, useState } from "react";

import "./toolbar.css";

declare global {
  interface Performance {
    memory?: {
      usedJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  }
}

export function DevToolbar() {
  const buildId =
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF ??
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ??
    "#local";

  return (
    <div
      style={{
        backgroundColor: "#222",
        color: "#fff",
        padding: "6px 24px",
        fontFamily: "monospace",
        fontSize: "12px",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
        boxShadow: "0 0 0 0.5px rgb(67, 67, 67)",
        isolation: "isolate",
      }}>
      <h1>ChainPatrol ({buildId})</h1>
      <DevToolsPanelSpacer />
      <DevToolsPanelFPS />
      <DevToolsPanelMemory />
      <DevToolsPanelCPU />
      <DevToolsPanelServices />
      <DevToolsPanelReactQuery />
    </div>
  );
}

export function DevToolsPanelSpacer() {
  return <div style={{ flex: 1 }}></div>;
}

export function DevToolsPanelReactQuery() {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <button
        style={{
          backgroundColor: "#444",
          color: "#fff",
          padding: "2px 8px",
          border: "none",
          cursor: "pointer",
          fontFamily: "monospace",
          fontSize: "12px",
        }}
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}>
        {isOpen ? "Close React Query" : "Open React Query"}
      </button>
      {isOpen && <ReactQueryDevtoolsPanel style={{ width: "100%" }} />}
    </>
  );
}

export function DevToolsPanelServices() {
  const items = [
    {
      name: "Mailing",
      url: "http://localhost:8025",
    },
    {
      name: "Inngest",
      url: "http://localhost:8288",
    },
    {
      name: "Temporal",
      url: "http://localhost:8233",
    },
  ];
  return (
    <div style={{ position: "relative" }}>
      <button
        style={{
          backgroundColor: "#444",
          color: "#fff",
          padding: "2px 8px",
          border: "none",
          cursor: "pointer",
          fontFamily: "monospace",
          fontSize: "12px",
        }}
        popoverTarget="services-popover">
        Services
      </button>
      <div id="services-popover" popover="auto">
        {items.map((item) => (
          <a
            key={item.name}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#fff", textDecoration: "none" }}>
            {item.name}
          </a>
        ))}
      </div>
    </div>
  );
}

export function DevToolsPanelCPU() {
  const [cpuUsage, setCpuUsage] = useState(0);

  useEffect(() => {
    let lastTime = performance.now();
    const interval = setInterval(() => {
      const currentTime = performance.now();
      const busyTime = Math.max(0, currentTime - lastTime - 1000);
      lastTime = currentTime;
      setCpuUsage(busyTime);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ width: "100px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
        }}>
        <h2 style={{ color: "#aaa" }}>CPU</h2>
        <p style={{ fontWeight: 600 }}>{cpuUsage.toFixed(2)} ms</p>
      </div>
      <div
        style={{
          width: "100%",
          height: "2px",
          backgroundColor: "#444",
          overflow: "hidden",
        }}>
        <div
          style={{
            width: `${(cpuUsage / 100) * 100}%`,
            height: "100%",
            backgroundColor: "#ffffff",
          }}></div>
      </div>
    </div>
  );
}

function getMemoryUsage() {
  if (!performance.memory) {
    console.warn("Memory API not supported");
    return {
      used: 0,
      total: 0,
    };
  }
  const used = performance.memory.usedJSHeapSize;
  const total = performance.memory.jsHeapSizeLimit;
  return {
    used,
    total,
  };
}

export function DevToolsPanelMemory() {
  const [memoryUsage, setMemoryUsage] = useState({
    used: 0,
    total: 0,
  });

  useEffect(() => {
    if (!performance.memory) {
      console.warn("Memory API not supported");
      return;
    }

    const interval = setInterval(() => {
      setMemoryUsage(getMemoryUsage());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const memoryUsageInMB = Math.round(memoryUsage.used / 1024 / 1024);
  const memoryUsagePercentage =
    memoryUsage.total <= 0 ? 0 : Math.round((memoryUsage.used / memoryUsage.total) * 100);

  return (
    <div style={{ width: "100px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
        }}>
        <h2 style={{ color: "#aaa" }}>MEM</h2>
        <p style={{ fontWeight: 600 }}>{memoryUsageInMB} MB</p>
      </div>
      <div
        style={{
          width: "100%",
          height: "2px",
          backgroundColor: "#444",
          overflow: "hidden",
        }}>
        <div
          style={{
            width: `${memoryUsagePercentage}%`,
            height: "100%",
            backgroundColor: "#ffffff",
          }}></div>
      </div>
    </div>
  );
}

export function DevToolsPanelFPS() {
  const [fps, setFps] = useState(0);

  useEffect(() => {
    let lastTime = performance.now();
    let frameCount = 0;

    const interval = setInterval(() => {
      const currentTime = performance.now();
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      setFps((frameCount / deltaTime) * 1000);
      frameCount = 0;
    }, 1000);

    const animate = () => {
      frameCount++;
      requestAnimationFrame(animate);
    };

    animate();

    return () => clearInterval(interval);
  }, []);

  const defaultFps =
    typeof window !== "undefined" && window.devicePixelRatio > 1 ? 120 : 60;

  return (
    <div style={{ width: "100px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
        }}>
        <h2 style={{ color: "#aaa" }}>FPS</h2>
        <p style={{ fontWeight: 600 }}>{Math.round(fps)}</p>
      </div>
      <div
        style={{
          width: "100%",
          height: "2px",
          backgroundColor: "#444",
          overflow: "hidden",
        }}>
        <div
          style={{
            width: `${(fps / defaultFps) * 100}%`,
            height: "100%",
            backgroundColor: "#ffffff",
          }}></div>
      </div>
    </div>
  );
}
